import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Card, PageHeader, Button, Modal, Input, Select, Spinner, SearchBar, EmptyState, Pill, fmtEur } from "@/components/common";
import { Plus, Trash2, Euro, CreditCard, Check } from "lucide-react";
import { PayPalPay } from "@/components/PayPalPay";

const STATO_CLS = { saldato: "bg-emerald-100 text-emerald-700", parziale: "bg-amber-100 text-amber-700", sospeso: "bg-red-100 text-red-700" };

export default function Pagamenti() {
  const { can } = useAuth();
  const editable = can("pagamenti", "edit");
  const [items, setItems] = useState(null);
  const [persone, setPersone] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ person_id: "", descrizione: "Quota annuale 2025/2026", importo_totale: 300, n_rate: 3 });
  const [pp, setPp] = useState(null);

  const load = () => {
    api.get("/pagamenti").then((r) => setItems(r.data)).catch((e) => toast.error(apiErr(e)));
    api.get("/persone").then((r) => setPersone(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.person_id) { toast.error("Seleziona l'atleta"); return; }
    const tot = Number(form.importo_totale); const n = Number(form.n_rate) || 1;
    const each = Math.round((tot / n) * 100) / 100;
    const rate = Array.from({ length: n }).map((_, i) => ({ numero: i + 1, importo: i === n - 1 ? Math.round((tot - each * (n - 1)) * 100) / 100 : each, pagata: false }));
    try { await api.post("/pagamenti", { person_id: form.person_id, descrizione: form.descrizione, importo_totale: tot, stagione: "2025/2026", rate }); toast.success("Piano rate creato"); setOpen(false); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const toggle = async (pid, num) => { try { await api.post(`/pagamenti/${pid}/rata/${num}/toggle`); load(); } catch (e) { toast.error(apiErr(e)); } };
  const payOnline = (p, r) => setPp({ kind: "rata", pagamento_id: p.id, rata_numero: r.numero, label: `${p.descrizione} — Rata ${r.numero}`, amount: r.importo });
  const del = async (id) => { if (!window.confirm("Eliminare?")) return; await api.delete(`/pagamenti/${id}`); load(); };

  if (!items) return <Spinner />;
  const totCrediti = items.reduce((s, p) => s + (p.residuo || 0), 0);
  const filtered = items.filter((p) => p.person_name.toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <PageHeader title="Quote e Pagamenti" subtitle="Rate, insoluti e pagamenti online"
        actions={editable && <Button onClick={() => setOpen(true)} data-testid="add-pagamento-btn"><Plus size={16} /> Nuovo piano quote</Button>} />
      <Card className="p-5 mb-5 bg-slate-900 border-slate-900" data-testid="crediti-banner">
        <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Crediti da incassare</p>
        <p className="text-3xl font-bold font-display text-white mt-1">{fmtEur(totCrediti)}</p>
      </Card>
      <div className="mb-4"><SearchBar value={q} onChange={setQ} placeholder="Cerca atleta..." /></div>
      {filtered.length === 0 ? <Card className="p-6"><EmptyState icon={Euro} title="Nessun pagamento" /></Card> : (
        <div className="space-y-4">
          {filtered.map((p) => (
            <Card key={p.id} className="p-5" data-testid={`pagamento-card-${p.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-semibold text-slate-800">{p.person_name}</h3>
                  <p className="text-sm text-slate-500">{p.descrizione} · Totale {fmtEur(p.importo_totale)}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Pill className={STATO_CLS[p.stato]}>{p.stato}</Pill>
                  {editable && <button onClick={() => del(p.id)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-500"><Trash2 size={15} /></button>}
                </div>
              </div>
              <div className="grid sm:grid-cols-3 gap-3">
                {p.rate.map((r) => (
                  <div key={r.numero} className={`rounded-lg border p-3 ${r.pagata ? "border-emerald-200 bg-emerald-50/50" : "border-slate-200"}`} data-testid={`rata-${p.id}-${r.numero}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-slate-500">Rata {r.numero}</span>
                      <span className={`text-sm font-bold ${r.pagata ? "text-emerald-600" : "text-slate-700"}`}>{fmtEur(r.importo)}</span>
                    </div>
                    {editable ? (
                      <div className="flex gap-1.5">
                        <button onClick={() => toggle(p.id, r.numero)} data-testid={`toggle-rata-${p.id}-${r.numero}`}
                          className={`flex-1 text-xs py-1.5 rounded-lg font-medium ${r.pagata ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                          {r.pagata ? <span className="flex items-center justify-center gap-1"><Check size={12} /> Pagata</span> : "Segna pagata"}
                        </button>
                        {!r.pagata && <button onClick={() => payOnline(p, r)} title="Paga con PayPal" data-testid={`pay-online-${p.id}-${r.numero}`} className="px-2 rounded-lg bg-sky-600 text-white hover:bg-sky-700"><CreditCard size={14} /></button>}
                      </div>
                    ) : <p className={`text-xs ${r.pagata ? "text-emerald-600" : "text-slate-400"}`}>{r.pagata ? "Pagata" : "Da pagare"}</p>}
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </div>
      )}
      <Modal open={open} onClose={() => setOpen(false)} title="Nuovo piano quote">
        <div className="space-y-4">
          <Select label="Atleta / Socio" data-testid="pag-person" value={form.person_id} onChange={(e) => setForm((f) => ({ ...f, person_id: e.target.value }))}>
            <option value="">Seleziona...</option>{persone.map((p) => <option key={p.id} value={p.id}>{p.cognome} {p.nome}</option>)}
          </Select>
          <Input label="Descrizione" value={form.descrizione} onChange={(e) => setForm((f) => ({ ...f, descrizione: e.target.value }))} />
          <div className="grid grid-cols-2 gap-4">
            <Input label="Importo totale €" type="number" value={form.importo_totale} onChange={(e) => setForm((f) => ({ ...f, importo_totale: e.target.value }))} />
            <Input label="Numero rate" type="number" min="1" max="12" value={form.n_rate} onChange={(e) => setForm((f) => ({ ...f, n_rate: e.target.value }))} />
          </div>
          <div className="flex justify-end gap-2"><Button variant="ghost" onClick={() => setOpen(false)}>Annulla</Button><Button onClick={create} data-testid="save-pagamento-btn">Crea piano</Button></div>
        </div>
      </Modal>
      <PayPalPay open={!!pp} order={pp || {}} onClose={() => setPp(null)} onSuccess={load} />
    </div>
  );
}
